import { Calendar, Clock, AlertTriangle, CheckCircle2, Timer } from 'lucide-react';
import React from 'react';
import { useGetScheduleSummaryQuery, useGetTaskSummaryQuery } from '../../services/dashboardApi';
import { WidgetProps } from './types';
import { WidgetLoadingSkeleton, WidgetEmptyState } from './WidgetErrorBoundary';

const ScheduleWidget: React.FC<WidgetProps> = ({ scope, projectId, size }) => {
  // Use schedule summary for project scope, task summary for org scope
  const {
    data: scheduleData,
    isLoading: isScheduleLoading,
    error: scheduleError,
  } = useGetScheduleSummaryQuery(
    { projectId: projectId! },
    { skip: scope !== 'project' || !projectId, pollingInterval: 300000 }
  );

  const {
    data: taskData,
    isLoading: isTaskLoading,
    error: taskError,
  } = useGetTaskSummaryQuery(
    { projectId: scope === 'project' ? projectId : undefined },
    { pollingInterval: 300000 }
  );

  const isLoading = scope === 'project' ? isScheduleLoading : isTaskLoading;
  const error = scope === 'project' ? scheduleError : taskError;

  if (isLoading) {
    return <WidgetLoadingSkeleton lines={4} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600 text-sm font-medium">Failed to load schedule data</p>
          <p className="text-gray-500 text-xs mt-1">Please try refreshing</p>
        </div>
      </div>
    );
  }

  // Render project-specific schedule
  if (scope === 'project' && scheduleData) {
    const completionPercent = scheduleData.completionPercentage || 0;
    const isCompact = size === '1x1';

    return (
      <div className="space-y-4">
        {/* Completion Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-900">Project Progress</span>
            <span className="text-lg font-bold text-gray-900">{completionPercent.toFixed(0)}%</span>
          </div>
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-500"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
        </div>

        {/* Task Status Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-green-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-gray-600">Completed</span>
            </div>
            <p className="text-xl font-bold text-green-700">{scheduleData.completedTasks}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Timer className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-medium text-gray-600">In Progress</span>
            </div>
            <p className="text-xl font-bold text-blue-700">{scheduleData.inProgressTasks}</p>
          </div>
        </div>

        {/* Overdue Warning */}
        {scheduleData.overdueTasks > 0 && (
          <div className="bg-red-50 border border-red-100 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-700">
                {scheduleData.overdueTasks} overdue task{scheduleData.overdueTasks !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Additional Stats (for larger sizes) */}
        {!isCompact && (
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-200">
            <div className="text-center">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-sm font-bold text-gray-900">{scheduleData.totalTasks}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Days Left</p>
              <p className="text-sm font-bold text-gray-900">{scheduleData.daysRemaining}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Critical Path</p>
              <p className="text-sm font-bold text-red-600">{scheduleData.criticalPathTasks}</p>
            </div>
          </div>
        )}

        {/* Float Analysis (only for 2x2) */}
        {size === '2x2' && (
          <div className="pt-2 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-700 mb-2">Schedule Health</p>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-600">
                  Float: <span className="font-medium">{scheduleData.floatDays} days</span>
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-600">
                  Not Started: <span className="font-medium">{scheduleData.notStartedTasks}</span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render org-level task summary
  if (taskData) {
    const totalTasks = taskData.total || 0;
    const isCompact = size === '1x1';

    return (
      <div className="space-y-4">
        {/* Task Status Overview */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-yellow-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Timer className="h-4 w-4 text-yellow-600" />
              <span className="text-xs font-medium text-gray-600">In Progress</span>
            </div>
            <p className="text-xl font-bold text-yellow-700">
              {taskData.byStatus?.in_progress || 0}
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-gray-600">Completed</span>
            </div>
            <p className="text-xl font-bold text-green-700">{taskData.byStatus?.completed || 0}</p>
          </div>
        </div>

        {/* Overdue & Due This Week */}
        <div className="grid grid-cols-2 gap-3">
          {taskData.overdue > 0 && (
            <div className="bg-red-50 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-1">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className="text-xs font-medium text-gray-600">Overdue</span>
              </div>
              <p className="text-xl font-bold text-red-700">{taskData.overdue}</p>
            </div>
          )}
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Calendar className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-medium text-gray-600">Due This Week</span>
            </div>
            <p className="text-xl font-bold text-blue-700">{taskData.dueThisWeek || 0}</p>
          </div>
        </div>

        {/* Priority Breakdown (for larger sizes) */}
        {!isCompact && taskData.byPriority && (
          <div className="pt-2 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-700 mb-2">By Priority</p>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-xs text-gray-600">
                  High: <span className="font-medium">{taskData.byPriority.high || 0}</span>
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500" />
                <span className="text-xs text-gray-600">
                  Medium: <span className="font-medium">{taskData.byPriority.medium || 0}</span>
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-xs text-gray-600">
                  Low: <span className="font-medium">{taskData.byPriority.low || 0}</span>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Total Summary */}
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Total Tasks: <span className="font-semibold text-gray-900">{totalTasks}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <WidgetEmptyState
      title="No Schedule Data"
      description="Schedule information is not available"
      icon={Calendar}
    />
  );
};

export default ScheduleWidget;
